import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ValueResponseDto } from '../dto/response/value-response.dto';
import { ValueSaveRequestDto } from '../dto/request/value-save-request.dto';
import { ValueUpdateRequestDto } from '../dto/request/value-update-request.dto';
import contributionService from '../services/contribution.service';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';

const initialState = {
  status: 'idle',
  contributions: [] as ValueResponseDto[] | null,
  contribution: {} as ValueResponseDto | null,
  error: null as string | null,
};

export const fetchContributionById = createAsyncThunk(
  'contributions/fetchContributionById',
  async (id: string) => {
    return await contributionService.fetchContributionById(id);
  }
);

export const saveContribution = createAsyncThunk(
  'contributions/saveContribution',
  async (saveRequest: ValueSaveRequestDto, { rejectWithValue }) => {
    try {
      return await contributionService.saveContribution(saveRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const updateContribution = createAsyncThunk(
  'contributions/updateContribution',
  async (updateRequest: ValueUpdateRequestDto, { rejectWithValue }) => {
    try {
      return await contributionService.updateContribution(updateRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

// export const updateVerificationLabel = createAsyncThunk(
//   'contributions/updateVerificationLabel',
//   async (updateRequest: ValueUpdateRequestDto) => {
//     return await contributionService.updateVerificationLabel(updateRequest);
//   }
// );

export const deleteContribution = createAsyncThunk(
  'contributions/deleteContribution',
  async (existingId: string, { rejectWithValue }) => {
    try {
      return await contributionService.deleteContribution(existingId);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const contributionSlice = createSlice({
  name: 'contributionSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContributionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchContributionById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contribution = action.payload;
      })
      .addCase(fetchContributionById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveContribution.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveContribution.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Contribution added successfully.');
        state.contributions?.push(action.payload);
      })
      .addCase(saveContribution.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Saving contribution failed. ${action.payload as string}`);
      })
      .addCase(updateContribution.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateContribution.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Contribution saved successfully.');
        if (state.contributions) {
          const contributionIndex = state.contributions.findIndex(
            (contribution) => contribution.id === action.payload.id
          );
          if (contributionIndex !== -1) {
            state.contributions[contributionIndex] = action.payload;
          }
        }
      })
      .addCase(updateContribution.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Updating contribution failed. ${action.payload as string}`);
      })
      // .addCase(updateVerificationLabel.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(updateVerificationLabel.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   if (state.contributions) {
      //     const contributionIndex = state.contributions.findIndex(
      //       (contribution) => contribution.id === action.payload.id
      //     );
      //     if (contributionIndex !== -1) {
      //       state.contributions[contributionIndex] = action.payload;
      //     }
      //   }
      // })
      // .addCase(updateVerificationLabel.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(deleteContribution.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteContribution.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Contribution deleted successfully.');
      })
      .addCase(deleteContribution.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting contribution failed. ${action.payload as string}`);
      });
  },
});

export default contributionSlice.reducer;
